import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Row, TextInput } from 'enable-ui';

import InviteeDropdown from 'modules/calendar/components/InviteeDropdown';
import { InviteeGroup } from 'modules/calendar/components/InviteeGroup';
import SelectedInvitees from 'modules/calendar/components/SelectedInvitees';
import type { Invitee } from 'modules/calendar/core/type';

import { ReactComponent as UserIcon } from 'assets/icons/learners.svg';

interface InviteeSelectorProps {
  selectedInvitees: Invitee[];
  onInviteeChange: (invitees: Invitee[]) => void;
  availableInvitees: {
    clients: {
      items: Invitee[];
    };
    trainers: {
      items: Invitee[];
    };
  };
  className?: string;
  onLoadMoreClients: () => void;
  onLoadMoreTrainers: () => void;
  isLoadingClients: boolean;
  isLoadingTrainers: boolean;
  hasMoreClients: boolean;
  hasMoreTrainers: boolean;
  onTabChange?: (tab: 'clients' | 'trainers') => void;
  onDropdownOpen?: () => void;
  allowLoadMore?: boolean;
  isShowInviteesWithoutSelected?: boolean;
}

const InviteeSelector: React.FC<InviteeSelectorProps> = ({
  selectedInvitees,
  onInviteeChange,
  availableInvitees,
  className,
  onLoadMoreClients,
  onLoadMoreTrainers,
  isLoadingClients,
  isLoadingTrainers,
  hasMoreClients,
  hasMoreTrainers,
  onTabChange,
  onDropdownOpen,
  allowLoadMore = true,
  isShowInviteesWithoutSelected,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'clients' | 'trainers'>('clients');
  const [hasInitializedTabs, setHasInitializedTabs] = useState<{
    clients: boolean;
    trainers: boolean;
  }>({
    clients: false,
    trainers: false,
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isDropdownOpen) {
      if (activeTab === 'clients' && !hasInitializedTabs.clients) {
        onLoadMoreClients();
        setHasInitializedTabs((prev) => ({ ...prev, clients: true }));
      }

      if (activeTab === 'trainers' && !hasInitializedTabs.trainers) {
        onLoadMoreTrainers();
        setHasInitializedTabs((prev) => ({ ...prev, trainers: true }));
      }
    }
  }, [
    isDropdownOpen,
    activeTab,
    hasInitializedTabs,
    onLoadMoreClients,
    onLoadMoreTrainers,
    isShowInviteesWithoutSelected,
  ]);

  useEffect(() => {
    if (!isDropdownOpen) {
      setHasInitializedTabs({ clients: false, trainers: false });
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isDropdownOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        handleCloseDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!allowLoadMore) return;

      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 20;

      if (!isNearBottom) return;

      if (activeTab === 'clients' && hasMoreClients && !isLoadingClients) {
        onLoadMoreClients();
      }

      if (activeTab === 'trainers' && hasMoreTrainers && !isLoadingTrainers) {
        onLoadMoreTrainers();
      }
    },
    [
      activeTab,
      allowLoadMore,
      hasMoreClients,
      hasMoreTrainers,
      isLoadingClients,
      isLoadingTrainers,
      onLoadMoreClients,
      onLoadMoreTrainers,
    ],
  );

  const handleCloseDropdown = () => setIsDropdownOpen(false);

  const handleChangeTab = (tab: 'clients' | 'trainers') => {
    if (tab !== activeTab) {
      setActiveTab(tab);
    }
  };

  const handleRemoveInvitee = (invitee: Invitee) => () => {
    onInviteeChange(selectedInvitees.filter((i) => i.id !== invitee.id));
  };

  const handleInputClick = () => {
    if (!isDropdownOpen) {
      setIsDropdownOpen(true);
      onDropdownOpen?.();
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleSelect = useCallback(
    (invitee: Invitee) => {
      const isSelected = selectedInvitees.some((i) => i.id === invitee.id);

      if (isSelected) return;

      onInviteeChange([...selectedInvitees, invitee]);
    },
    [selectedInvitees, onInviteeChange],
  );

  if (isShowInviteesWithoutSelected) {
    const trainers = selectedInvitees.filter((invitee) => invitee.type === 'trainer');
    const clients = selectedInvitees.filter((invitee) => invitee.type === 'client');

    return (
      <div
        style={{ display: 'grid', gridTemplateColumns: '9px 1fr' }}
        className={`relative ${className}`}
        ref={containerRef}
      >
        <UserIcon />
        <Row direction="column" style={{ transform: 'translateY(-20px)', gap: 20 }}>
          {trainers.length > 0 && (
            <InviteeGroup title={`${trainers.length} trainers`} items={trainers} />
          )}

          {clients.length > 0 && (
            <InviteeGroup title={`${clients.length} clients`} items={clients} />
          )}
        </Row>
      </div>
    );
  }

  return (
    <Row direction="column" className={`relative ${className}`} ref={containerRef}>
      <Row align="center" className="gap-16">
        <UserIcon />

        <TextInput
          inputRef={inputRef}
          onClick={handleInputClick}
          aria-expanded={isDropdownOpen}
          aria-haspopup="dialog"
          placeholder="Add invitees"
          value={searchQuery}
          onChange={handleSearchChange}
        />
      </Row>

      <InviteeDropdown
        isOpen={isDropdownOpen}
        onClose={handleCloseDropdown}
        anchorEl={inputRef.current}
        selectedInvitees={selectedInvitees}
        availableInvitees={availableInvitees}
        onSelect={handleSelect}
        activeTab={activeTab}
        setActiveTab={handleChangeTab}
        onScroll={handleScroll}
        isLoadingClients={isLoadingClients}
        isLoadingTrainers={isLoadingTrainers}
        onTabChange={onTabChange as any}
        dropdownRef={dropdownRef}
        searchQuery={searchQuery}
      />

      {selectedInvitees.length > 0 && (
        <SelectedInvitees
          trainers={{
            items: selectedInvitees.filter((i) => i.type === 'trainer'),
          }}
          clients={{
            items: selectedInvitees.filter((i) => i.type === 'client'),
          }}
          onRemoveInvitee={handleRemoveInvitee}
        />
      )}
    </Row>
  );
};

export default InviteeSelector;
